import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { billingAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, 
  Mail, 
  CreditCard, 
  Zap,
  Check,
  Loader2,
  BarChart3,
  Crown,
  Book,
  Download,
  ExternalLink,
  Lock,
  Eye,
  EyeOff,
  KeyRound
} from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';
import SecuritySessions from '@/components/settings/SecuritySessions';
import TrustedDevices from '@/components/settings/TrustedDevices';
import { toast } from 'sonner';
import api from '@/services/api';

export const SettingsPage = () => {
  const { user, featureAccess } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [manualInfo, setManualInfo] = useState(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Stable ref for recursive setTimeout in checkPaymentStatus.
  // Avoids stale-closure bug from useCallback([]) referencing itself.
  const checkPaymentStatusRef = useRef(null);
  
  // Get initial tab from URL params
  const initialTab = searchParams.get('tab') || 'profile';

  const loadBillingData = useCallback(async () => {
    try {
      const [subRes, plansRes, usageRes, manualRes] = await Promise.all([
        billingAPI.getSubscription(),
        billingAPI.getPlans(),
        billingAPI.getUsage(),
        api.get('/docs/user-manual/info').catch(() => ({ data: null }))
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
      setUsage(usageRes.data);
      if (manualRes?.data) setManualInfo(manualRes.data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
    // ``api`` and ``billingAPI`` are module-scope imports; setters are
    // stable — no reactive deps needed.
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    setCheckingPayment(true);
    try {
      const response = await billingAPI.getCheckoutStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success(`Successfully upgraded to ${response.data.tier?.charAt(0).toUpperCase() + response.data.tier?.slice(1) || 'new plan'}!`);
        loadBillingData();
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/dashboard/settings?tab=billing');
        return;
      }
      // Still pending - retry
      if (attempt < maxAttempts) {
        setTimeout(() => checkPaymentStatusRef.current?.(sessionId, attempt + 1), 2000);
        return;
      }
      toast.info('Payment is being processed. Your plan will update shortly.');
    } catch (error) {
      console.error('Payment status error:', error);
    }
    setCheckingPayment(false);
    window.history.replaceState({}, '', '/dashboard/settings?tab=billing');
  }, [loadBillingData]);

  // Keep the ref in sync so the recursive setTimeout always uses the latest fn
  useEffect(() => {
    checkPaymentStatusRef.current = checkPaymentStatus;
  }, [checkPaymentStatus]);

  useEffect(() => {
    loadBillingData();
    
    // Check for payment completion
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, loadBillingData, checkPaymentStatus]);

  const handleUpgrade = async (tier, billingCycle = 'monthly') => {
    try {
      // For paid plans, use the subscription API directly
      const response = await api.post('/billing/checkout', { 
        tier, 
        billing_cycle: billingCycle, 
        origin_url: window.location.origin 
      });
      
      if (response.data.url) {
        // Redirect to pricing page for payment
        window.location.href = response.data.url;
      } else if (response.data.status === 'success') {
        // Plan activated directly (free plan or demo mode)
        toast.success(response.data.message || 'Plan updated successfully!');
        loadBillingData();
      } else if (response.data.subscription_id) {
        // Custom plan activated
        toast.success(`Plan activated successfully!`);
        loadBillingData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan. Please try again.');
    }
  };
  
  const handleCustomPlanSubscribe = async (customConfig) => {
    try {
      const response = await api.post('/billing/checkout', {
        plan_id: `custom_${Date.now()}`,
        billing_cycle: 'monthly',
        origin_url: window.location.origin,
        custom_config: customConfig
      });
      
      if (response.data.status === 'success') {
        toast.success(`Custom plan activated at $${response.data.monthly_price}/month!`);
        loadBillingData();
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create custom plan');
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'starter': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'professional': return 'bg-teal-100 text-teal-700 border-teal-200';
      case 'enterprise': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getTierGradient = (tier) => {
    switch (tier) {
      case 'free': return 'from-slate-100 to-slate-50';
      case 'starter': return 'from-blue-100 to-blue-50';
      case 'professional': return 'from-teal-100 to-emerald-50';
      case 'enterprise': return 'from-amber-100 to-yellow-50';
      default: return 'from-slate-100 to-slate-50';
    }
  };

  const getUsagePercent = (used, limit) => {
    if (limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const formatLimit = (limit) => {
    if (limit === -1) return 'Unlimited';
    return limit.toLocaleString();
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your account and subscription</p>
        </div>
        <HelpButton 
          title="Settings & Billing"
          content={`
            <p>Manage your profile, subscription, and usage limits here.</p>
            <p class="mt-2"><strong>Tabs:</strong></p>
            <ul>
              <li><strong>Profile:</strong> View your account information</li>
              <li><strong>Billing:</strong> Manage your subscription plan and upgrade</li>
              <li><strong>Usage:</strong> Track your resource consumption (AI agents, messages, documents)</li>
            </ul>
            <p class="mt-2"><strong>Upgrading:</strong> Click "Upgrade" on any plan to start a subscription. You'll be redirected to our secure payment page.</p>
            <p class="mt-2"><strong>Usage Limits:</strong> Each plan has different limits. Monitor your usage to avoid interruptions.</p>
          `}
        />
      </div>

      {checkingPayment && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
            <p className="text-indigo-700">Verifying your payment...</p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={initialTab} className="space-y-6">
        <TabsList className="bg-slate-100 border border-slate-200 p-1 flex-wrap">
          <TabsTrigger value="profile" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <User className="w-4 h-4 mr-2" />Profile
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <CreditCard className="w-4 h-4 mr-2" />Billing
          </TabsTrigger>
          <TabsTrigger value="usage" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />Usage
          </TabsTrigger>
          <TabsTrigger value="help" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Book className="w-4 h-4 mr-2" />Help
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-slate-900">Profile</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-blue-600 text-white text-2xl">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{user?.name}</h3>
                  <p className="text-slate-600">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">User ID</p>
                    <p className="text-slate-900 font-mono text-sm">{user?.user_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="text-slate-900">{user?.email}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Change Password Card */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-600" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-600">New Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-600">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                  data-testid="confirm-password-input"
                />
              </div>
              
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
              
              <Button 
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Active Sessions & Devices */}
          <SecuritySessions />

          {/* Trusted Devices (iter124) */}
          <TrustedDevices />
          
          {/* Feature Access */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" />
                Your Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'leads_crm', label: 'Leads CRM', desc: 'Track and manage leads' },
                  { key: 'analytics', label: 'Analytics', desc: 'Performance insights' },
                  { key: 'automations', label: 'Automations', desc: 'Follow-up sequences' },
                  { key: 'email_channel', label: 'Email Channel', desc: 'Email integration' },
                  { key: 'booking', label: 'Booking', desc: 'Calendar scheduling' },
                  { key: 'api_integrations', label: 'API Access', desc: 'Custom integrations' },
                  { key: 'white_label', label: 'White Label', desc: 'Custom branding' },
                  { key: 'priority_support', label: 'Priority Support', desc: 'Fast response' }
                ].map(({ key, label, desc }) => {
                  const hasAccess = user?.role === 'system_admin' || featureAccess?.[key];
                  return (
                    <div 
                      key={key} 
                      className={`p-3 rounded-lg border ${hasAccess ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {hasAccess ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Lock className="w-4 h-4 text-slate-400" />
                        )}
                        <span className={`font-medium ${hasAccess ? 'text-emerald-700' : 'text-slate-500'}`}>{label}</span>
                      </div>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                  );
                })}
              </div>
              {(!featureAccess || Object.values(featureAccess).every(v => !v)) && user?.role !== 'system_admin' && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <Zap className="w-4 h-4 inline mr-2" />
                    Upgrade your plan to unlock more features!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          {/* Current Plan */}
          <Card className={`border-2 shadow-sm overflow-hidden ${subscription?.tier === 'professional' ? 'border-teal-300' : subscription?.tier === 'enterprise' ? 'border-amber-300' : subscription?.tier === 'starter' ? 'border-blue-300' : 'border-slate-200'}`}>
            <div className={`bg-gradient-to-r ${getTierGradient(subscription?.tier || 'free')} px-6 py-2`}>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Plan</span>
            </div>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getTierGradient(subscription?.tier || 'free')} flex items-center justify-center border`}>
                      <Crown className={`w-7 h-7 ${subscription?.tier === 'professional' ? 'text-teal-600' : subscription?.tier === 'enterprise' ? 'text-amber-600' : subscription?.tier === 'starter' ? 'text-blue-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-slate-900">{subscription?.plan_name || 'Free Plan'}</h3>
                        <Badge className={`${getTierColor(subscription?.tier || 'free')} border font-semibold`}>{(subscription?.tier || 'free').charAt(0).toUpperCase() + (subscription?.tier || 'free').slice(1)}</Badge>
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">Active</Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {subscription?.tier === 'free' 
                          ? 'Free forever with limited features' 
                          : `$${subscription?.price || subscription?.monthly_price || 0}/${subscription?.billing_cycle || 'month'}`
                        }
                      </p>
                    </div>
                  </div>
                  {subscription?.tier !== 'enterprise' && (
                    <Button 
                      onClick={() => document.getElementById('available-plans')?.scrollIntoView({ behavior: 'smooth' })}
                      variant="outline"
                      className="border-teal-300 text-teal-700 hover:bg-teal-50"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Plan
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Available Plans */}
          <div id="available-plans">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Available Plans</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => (
              <Card 
                key={plan.tier} 
                className={`bg-white shadow-sm transition-all ${
                  subscription?.tier === plan.tier 
                    ? 'ring-2 ring-teal-500 border-teal-300' 
                    : plan.tier === 'professional' 
                      ? 'border-teal-200 hover:border-teal-300' 
                      : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <CardContent className="p-6">
                  {subscription?.tier === plan.tier && (
                    <Badge className="bg-teal-600 text-white mb-3">Your Plan</Badge>
                  )}
                  {plan.tier === 'professional' && subscription?.tier !== plan.tier && (
                    <Badge className="bg-blue-600 text-white mb-3">Popular</Badge>
                  )}
                  <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                  <div className="mt-2 mb-4">
                    <span className="text-3xl font-bold text-slate-900">${plan.price_monthly}</span>
                    <span className="text-slate-500">/mo</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {formatLimit(plan.limits.max_chatbots_per_sub_account * plan.limits.max_sub_accounts)} AI agents
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {formatLimit(plan.limits.max_documents)} documents
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {formatLimit(plan.limits.max_messages_per_month)} messages/mo
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {formatLimit(plan.limits.max_team_members)} team members
                    </li>
                  </ul>
                  {subscription?.tier === plan.tier ? (
                    <Button disabled className="w-full bg-teal-100 text-teal-700 border border-teal-200 cursor-default">
                      <Check className="w-4 h-4 mr-2" />Current Plan
                    </Button>
                  ) : plan.tier === 'free' ? (
                    <Button disabled className="w-full bg-slate-100 text-slate-400">Free</Button>
                  ) : (
                    <Button 
                      onClick={() => handleUpgrade(plan.tier)}
                      className={plan.tier === 'professional' ? 'w-full bg-teal-600 hover:bg-teal-700' : 'w-full bg-slate-700 hover:bg-slate-800'}
                    >
                      Upgrade
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          </div>
          
          {/* Custom Plan Builder */}
          <CustomPlanBuilder onSubscribe={handleCustomPlanSubscribe} />
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900">Usage This Month</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading || !usage ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
              ) : (
                <>
                  <UsageItem 
                    label="Accounts" 
                    used={usage.usage.accounts.used} 
                    limit={usage.usage.accounts.limit} 
                  />
                  <UsageItem 
                    label="AI Employees" 
                    used={usage.usage.chatbots.used} 
                    limit={usage.usage.chatbots.limit} 
                  />
                  <UsageItem 
                    label="Documents" 
                    used={usage.usage.documents.used} 
                    limit={usage.usage.documents.limit} 
                  />
                  <UsageItem 
                    label="Messages" 
                    used={usage.usage.messages.used} 
                    limit={usage.usage.messages.limit} 
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Help Tab */}
        <TabsContent value="help" className="space-y-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Book className="w-5 h-5 text-blue-600" />
                User Manual & Documentation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-slate-600">
                Access comprehensive documentation to learn how to use Harmonix effectively. 
                The user manual covers all platform features with step-by-step guides and screenshots.
              </p>
              
              {/* Download/View Button */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={() => {
                    if (manualInfo?.has_document) {
                      window.open('/api/docs/user-manual/download', '_blank');
                    } else {
                      window.open('/docs/user-manual', '_blank');
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="download-manual-btn"
                >
                  {manualInfo?.has_document ? (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download User Manual
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View User Manual
                    </>
                  )}
                </Button>
                
                {manualInfo?.has_document && (
                  <Button 
                    variant="outline"
                    onClick={() => window.open('/docs/user-manual', '_blank')}
                    className="border-slate-300 text-slate-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Online Version
                  </Button>
                )}
              </div>
              
              {manualInfo?.version && (
                <p className="text-xs text-slate-500">
                  Version {manualInfo.version}
                </p>
              )}
              
              {/* Quick Help Topics */}
              <div className="border-t border-slate-200 pt-6">
                <h4 className="text-slate-900 font-medium mb-4">Quick Help Topics</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { title: 'Creating Your First AI Employee', desc: 'Learn how to create and configure AI agents', link: '/dashboard/chatbots' },
                    { title: 'Knowledge Base Management', desc: 'Upload documents, URLs, and manage your knowledge', link: '/dashboard/chatbots' },
                    { title: 'AI Employee Rules', desc: 'Define behavior rules for your AI agents', link: '/dashboard/chatbots' },
                    { title: 'Agent Settings & Calendar', desc: 'Configure booking settings per AI agent', link: '/dashboard/chatbots' },
                    { title: 'Billing & Plans', desc: 'Manage your subscription and view usage', link: '/dashboard/settings?tab=billing' },
                    { title: 'View Full Manual', desc: 'Complete documentation and guides', link: '/docs/user-manual', external: true },
                  ].map((topic) => (
                    <a
                      key={topic.link}
                      href={topic.link}
                      target={topic.external ? '_blank' : '_self'}
                      rel={topic.external ? 'noopener noreferrer' : undefined}
                      className="p-3 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-blue-300 transition-colors cursor-pointer block"
                    >
                      <h5 className="text-slate-800 font-medium text-sm flex items-center gap-2">
                        {topic.title}
                        {topic.external && <ExternalLink className="w-3 h-3 text-slate-400" />}
                      </h5>
                      <p className="text-xs text-slate-500 mt-1">{topic.desc}</p>
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Support Contact */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Need more help?</strong> Contact our support team at{' '}
                  <a href="mailto:support@harmonix.info" className="underline hover:text-blue-600">
                    support@harmonix.info
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const UsageItem = ({ label, used, limit }) => {
  const percent = limit === -1 ? 0 : Math.min((used / limit) * 100, 100);
  const isWarning = percent > 80;
  const isLimit = percent >= 100;
  
  return (
    <div>
      <div className="flex justify-between mb-2">
        <span className="text-slate-700">{label}</span>
        <span className={`text-sm ${isLimit ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-500'}`}>
          {used.toLocaleString()} / {limit === -1 ? 'Unlimited' : limit.toLocaleString()}
        </span>
      </div>
      <Progress 
        value={percent} 
        className={`h-2 ${isLimit ? '[&>div]:bg-red-500' : isWarning ? '[&>div]:bg-amber-500' : '[&>div]:bg-blue-500'}`}
      />
    </div>
  );
};

// Custom Plan Builder for Users
const CustomPlanBuilder = ({ onSubscribe }) => {
  const [config, setConfig] = useState({
    documents: 100,
    chatbots: 2,
    messages: 5000,
    storage_mb: 500
  });
  const [pricingConfig, setPricingConfig] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  
  const calculatePrice = useCallback(async () => {
    try {
      const response = await api.post('/pricing-config/calculate', config);
      setCalculatedPrice(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  }, [config]);
  
  useEffect(() => {
    const loadPricingConfig = async () => {
      try {
        const response = await api.get('/pricing-config/public');
        setPricingConfig(response.data);
      } catch (error) {
        console.error('Error loading pricing config:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPricingConfig();
  }, []);
  
  useEffect(() => {
    if (pricingConfig?.custom_plans_enabled) {
      calculatePrice();
    }
  }, [pricingConfig, calculatePrice]);
  
  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      await onSubscribe(config);
    } finally {
      setSubscribing(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  
  if (!pricingConfig?.custom_plans_enabled) {
    return null;
  }
  
  const limits = pricingConfig?.limits || {};
  
  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          Build Your Custom Plan
        </CardTitle>
        <p className="text-sm text-slate-600">
          Need more documents but fewer AI agents? Customize your plan to match your exact needs.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Sliders */}
          <div className="space-y-6">
            {/* Documents */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700">Documents</Label>
                <span className="text-sm font-semibold text-slate-900">{config.documents}</span>
              </div>
              <input
                type="range"
                min={limits.documents?.min || 10}
                max={limits.documents?.max || 1000}
                step={10}
                value={config.documents}
                onChange={(e) => setConfig({ ...config, documents: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            
            {/* AI Employees */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700">AI Employees</Label>
                <span className="text-sm font-semibold text-slate-900">{config.chatbots}</span>
              </div>
              <input
                type="range"
                min={limits.chatbots?.min || 1}
                max={limits.chatbots?.max || 50}
                step={1}
                value={config.chatbots}
                onChange={(e) => setConfig({ ...config, chatbots: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            
            {/* Messages */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700">Monthly Messages</Label>
                <span className="text-sm font-semibold text-slate-900">{config.messages.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min={limits.messages?.min || 1000}
                max={limits.messages?.max || 100000}
                step={1000}
                value={config.messages}
                onChange={(e) => setConfig({ ...config, messages: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            
            {/* Storage */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-700">Storage</Label>
                <span className="text-sm font-semibold text-slate-900">
                  {config.storage_mb >= 1000 ? `${(config.storage_mb / 1000).toFixed(1)}GB` : `${config.storage_mb}MB`}
                </span>
              </div>
              <input
                type="range"
                min={limits.storage_mb?.min || 100}
                max={limits.storage_mb?.max || 5000}
                step={100}
                value={config.storage_mb}
                onChange={(e) => setConfig({ ...config, storage_mb: parseInt(e.target.value) })}
                className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </div>
          
          {/* Price Summary */}
          <div className="bg-white rounded-xl border border-blue-200 p-6">
            <h4 className="font-semibold text-slate-900 mb-4">Your Custom Plan</h4>
            
            <div className="space-y-2 mb-6 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{config.documents} Documents</span>
                <span className="text-slate-700">${calculatedPrice?.breakdown?.documents?.price || '...'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{config.chatbots} AI Employees</span>
                <span className="text-slate-700">${calculatedPrice?.breakdown?.chatbots?.price || '...'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{config.messages.toLocaleString()} Messages</span>
                <span className="text-slate-700">${calculatedPrice?.breakdown?.messages?.price || '...'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{config.storage_mb}MB Storage</span>
                <span className="text-slate-700">${calculatedPrice?.breakdown?.storage_mb?.price || '...'}</span>
              </div>
            </div>
            
            <div className="pt-4 border-t border-slate-200">
              <div className="flex justify-between items-baseline">
                <span className="text-slate-700">Monthly Total</span>
                <div>
                  <span className="text-3xl font-bold text-slate-900">${calculatedPrice?.monthly_price || 0}</span>
                  <span className="text-slate-500">/mo</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">Billed every 30 days</p>
            </div>
            
            <Button
              onClick={handleSubscribe}
              disabled={subscribing || !calculatedPrice}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
              data-testid="subscribe-custom-plan-btn"
            >
              {subscribing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : (
                'Subscribe to Custom Plan'
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SettingsPage;
